import { useEffect, useState } from "react";
import { Menu, RefreshCw, Search, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ds";
import { CommandPalette } from "./CommandPalette";

/**
 * Top bar (56px): mobile menu trigger + centered command search (⌘K) + a
 * mobile refresh affordance. Backdrop-blurred. The search affordance opens
 * the global CommandPalette (festivals, riders, pages); ⌘K toggles it.
 */
export function TopBar({
  onMenu,
  onRefresh,
  isRefreshing,
}: {
  onMenu: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line-2 px-4 backdrop-blur md:px-6"
      style={{ background: "color-mix(in oklab, var(--tr-bg) 80%, transparent)" }}
    >
      <button
        type="button"
        className="iconbtn md:hidden"
        onClick={onMenu}
        aria-label="Open menu"
      >
        <Icon icon={Menu} size={18} />
      </button>

      <div className="flex flex-1 justify-center">
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          aria-haspopup="dialog"
          className="flex w-full max-w-[420px] cursor-text items-center gap-2 rounded-[9px] border border-line-2 bg-panel px-3 py-1.5 text-left transition-colors hover:border-line-3"
        >
          <Icon icon={Search} size={15} className="flex-none text-ink-3" />
          <span className="flex-1 truncate text-[13px] text-ink-4">
            Search festivals, riders, artists…
          </span>
          <span className="kbd hidden sm:inline">⌘K</span>
        </button>
      </div>

      <Link to="/learn" className="tour-launcher"><Compass size={15} />Explore Trace</Link>

      {onRefresh && (
        <button
          type="button"
          className="iconbtn md:hidden"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label="Refresh"
          title="Refresh"
        >
          <Icon icon={RefreshCw} size={18} className={isRefreshing ? "animate-spin" : ""} />
        </button>
      )}

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
